import { useMemo } from "react"
import { Link } from "react-router-dom"
import { ExternalLink } from "lucide-react"
import { CellContext } from "@tanstack/react-table"
import Table from "../../../../table"
import { AdvisorThread, BBColumnDef } from "../../../../../../types"
import { formatAdvisorDate } from "../../../../../utils/datetime"

const getLatestUserMessage = (thread: AdvisorThread): string =>
  [...thread.messages].reverse().find(m => m.role === "user")?.content ?? ""

interface ConversationsTableProps {
  threads: AdvisorThread[]
  sessionId: string
}

const ConversationsTable = ({
  threads,
  sessionId
}: ConversationsTableProps) => {
  const columns = useMemo<BBColumnDef<AdvisorThread>[]>(
    () => [
      {
        id: "lastActivity",
        header: "Last Activity",
        accessorFn: row => row.lastActivityAt,
        meta: { headerClassName: "py-4 pl-2 pr-3 text-left" },
        cell: ({ row }: CellContext<AdvisorThread, unknown>) => {
          const snippet = getLatestUserMessage(row.original)
          return (
            <td className="py-4 pl-2 pr-3">
              <time
                dateTime={row.original.lastActivityAt}
                className="text-sm font-medium text-neutral-700 block"
              >
                {formatAdvisorDate(row.original.lastActivityAt)}
              </time>
              <div className="sm:hidden text-xs text-neutral-400 mt-0.5">
                {row.original.messageCount} msgs
                {snippet ? <> &middot; &ldquo;{snippet}&rdquo;</> : null}
              </div>
            </td>
          )
        }
      },
      // TODO: re-enable insights column
      // {
      //   id: "insights",
      //   header: "Insights",
      //   meta: { headerClassName: "w-36 py-4 text-left" },
      //   cell: ({ row }: CellContext<AdvisorThread, unknown>) => (
      //     <td className="whitespace-nowrap py-4 pl-2 pr-3">
      //       <button
      //         type="button"
      //         className="px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-semibold rounded border border-indigo-100 hover:bg-indigo-100 transition-colors"
      //         onClick={() => console.log("View insights", row.original._id)}
      //       >
      //         View insights
      //       </button>
      //     </td>
      //   )
      // },
      {
        id: "snippet",
        header: "Latest Message",
        meta: { headerClassName: "hidden sm:table-cell py-4 text-center" },
        cell: ({ row }: CellContext<AdvisorThread, unknown>) => {
          const snippet = getLatestUserMessage(row.original)
          return (
            <td className="hidden sm:table-cell whitespace-nowrap py-4 cursor-pointer truncate max-w-0 text-sm text-center">
              {snippet ? (
                `"${snippet}"`
              ) : (
                <span className="text-neutral-300">—</span>
              )}
            </td>
          )
        }
      },
      {
        id: "totalMessages",
        header: "Messages",
        meta: { headerClassName: "hidden sm:table-cell w-16 py-4 text-center" },
        cell: ({ row }: CellContext<AdvisorThread, unknown>) => (
          <td className="hidden sm:table-cell py-4 pr-3 w-16 text-sm text-neutral-500 text-center">
            {row.original.messageCount}
          </td>
        )
      },
      {
        id: "action",
        header: "Action",
        meta: { headerClassName: "w-8 sr-only" },
        cell: () => (
          <td className="px-2 py-3 text-right">
            <Link
              target="_blank"
              to={`/conversations/advisor/${sessionId}`}
              className="inline-flex text-neutral-400 hover:text-purple-400 transition-colors"
              title="Open in Conversations"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </td>
        )
      }
    ],
    [sessionId]
  )

  return (
    <>
      <div className="py-4 border-b border-neutral-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold">AI-Driven Conversations</h3>
        <span className="text-xs">
          Total Conversations:{" "}
          <span className="font-semibold text-neutral-700">
            {threads.length}
          </span>
        </span>
      </div>

      {threads.length === 0 ? (
        <div className="py-12 text-center text-sm text-neutral-400">
          No conversation threads found.
        </div>
      ) : (
        <Table
          data={threads}
          viewType="table"
          searchBarVisible={false}
          noSorter
          columns={columns}
          tableContainerClassName="w-full overflow-auto"
        />
      )}
    </>
  )
}

export default ConversationsTable
